import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toValidObjectIdString } from '../utils/security';
import { fetchIadeOlusturKatalog, iadeFaturaOlustur } from '../services/iadeFaturaService';
import {
  fetchFormTaslak,
  saveFormTaslak,
  deleteFormTaslak,
  FORM_TASLAK_TIP_IADE,
} from '../services/formTaslakService';
import StokLimitliParcaPicker from './parcaSecim/StokLimitliParcaPicker';
import IntegerInput from './IntegerInput';
import ParcaPickerNestedModal from './ParcaPickerNestedModal';
import { parseAdet } from '../utils/integerInput';
import './SiparisOlusturModal.css';

const DRAFT_DEBOUNCE_MS = 2000;

const IadeOlusturModal = ({ onClose, onSuccess }) => {
  const [katalog, setKatalog] = useState([]);
  const [secilenParcalar, setSecilenParcalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [draftSavedHint, setDraftSavedHint] = useState(false);
  const [parcaPickerOpen, setParcaPickerOpen] = useState(false);
  const draftLoadedRef = useRef(false);
  const saveTimerRef = useRef(null);

  const loadKatalog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchIadeOlusturKatalog();
      setKatalog(res?.veri?.parcalar || []);
    } catch {
      setKatalog([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKatalog();
  }, [loadKatalog]);

  useEffect(() => {
    if (draftLoadedRef.current) return;
    draftLoadedRef.current = true;

    (async () => {
      try {
        const res = await fetchFormTaslak(FORM_TASLAK_TIP_IADE);
        const parcalar = res?.veri?.veri?.parcalar;
        if (Array.isArray(parcalar) && parcalar.length > 0) {
          const devam = window.confirm(
            'Kaydedilmiş bir iade taslağı bulundu. Taslaktan devam etmek ister misiniz?',
          );
          if (devam) {
            setSecilenParcalar(parcalar);
          } else {
            await deleteFormTaslak(FORM_TASLAK_TIP_IADE);
          }
        }
      } catch {
        /* taslak yok */
      }
    })();
  }, []);

  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        if (secilenParcalar.length === 0) {
          await deleteFormTaslak(FORM_TASLAK_TIP_IADE).catch(() => {});
          setDraftSavedHint(false);
          return;
        }
        await saveFormTaslak(FORM_TASLAK_TIP_IADE, { parcalar: secilenParcalar });
        setDraftSavedHint(true);
      } catch {
        /* sessiz */
      }
    }, DRAFT_DEBOUNCE_MS);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [secilenParcalar]);

  const handleSubmit = async () => {
    if (secilenParcalar.length === 0) {
      setError('En az bir parça seçmelisiniz');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const parcalarPayload = [];
      for (const p of secilenParcalar) {
        const icerikId = toValidObjectIdString(p.icerikId || p.id);
        if (!icerikId) {
          setError(`"${p.parcaAdi || 'Parça'}" için geçersiz ID formatı.`);
          setSubmitting(false);
          return;
        }
        parcalarPayload.push({ icerikId, adet: p.adet || 1 });
      }
      const res = await iadeFaturaOlustur(parcalarPayload);
      if (res?.basarili) {
        await deleteFormTaslak(FORM_TASLAK_TIP_IADE).catch(() => {});
        window.showNotification?.('success', res?.mesaj || 'İade talebi oluşturuldu');
        onSuccess?.();
        onClose();
      } else {
        setError(res?.mesaj || 'İade talebi oluşturulamadı');
      }
    } catch (err) {
      setError(err?.message || 'İade talebi oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDiscardDraft = async () => {
    if (!window.confirm('Taslak silinsin mi?')) return;
    await deleteFormTaslak(FORM_TASLAK_TIP_IADE).catch(() => {});
    setSecilenParcalar([]);
    setDraftSavedHint(false);
  };

  return (
    <div className="modal-overlay siparis-modal siparis-modal--parts" onClick={onClose}>
      <div className="modal-content siparis-modal__content--wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>İade Oluştur</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body siparis-modal__body--picker">
          <p className="siparis-modal-step-desc">
            Yalnızca stoktaki parçalar listelenir. Öz ve konsinye stok ayrımı parantez içinde gösterilir.
          </p>
          {draftSavedHint && (
            <p className="siparis-modal-draft-hint">
              <i className="fas fa-cloud-upload-alt" /> Taslak kaydedildi
            </p>
          )}
          <div className="parca-sec-open-picker-row">
            <button
              type="button"
              className="btn btn-secondary parca-sec-open-picker"
              onClick={() => setParcaPickerOpen(true)}
              disabled={loading}
            >
              <i className="fas fa-layer-group" aria-hidden /> Parça Seç
              {secilenParcalar.length > 0 ? (
                <span className="parca-picker-selected-badge-count" style={{ marginLeft: 4 }}>
                  ({secilenParcalar.length})
                </span>
              ) : null}
            </button>
          </div>
          <div className="parca-sec-table-wrap">
            {secilenParcalar.length === 0 ? (
              <div className="parca-sec-empty-positions">
                Henüz parça eklenmedi. «Parça Seç» ile stoktaki parçaları ekleyin.
              </div>
            ) : (
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>Parça</th>
                    <th style={{ textAlign: 'center' }}>Adet</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {secilenParcalar.map((p) => {
                    const kat = katalog.find((k) => String(k.icerikId) === String(p.icerikId));
                    const max = kat?.kullanilabilir ?? 999;
                    return (
                      <tr key={p.icerikId}>
                        <td>{p.parcaAdi}</td>
                        <td style={{ textAlign: 'center' }}>
                          <IntegerInput
                            min={1}
                            max={max}
                            className="parca-sec-qty-input"
                            value={p.adet || 1}
                            onChange={(v) => {
                              const num = parseAdet(v, { min: 1, max });
                              setSecilenParcalar((prev) =>
                                prev.map((x) =>
                                  String(x.icerikId) === String(p.icerikId)
                                    ? { ...x, adet: num }
                                    : x,
                                ),
                              );
                            }}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="category-action-btn delete-btn"
                            onClick={() =>
                              setSecilenParcalar((prev) =>
                                prev.filter((x) => String(x.icerikId) !== String(p.icerikId)),
                              )
                            }
                          >
                            <i className="fas fa-trash-alt" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <ParcaPickerNestedModal
            open={parcaPickerOpen}
            onClose={() => setParcaPickerOpen(false)}
            title="Parça seç — stoktaki parçalar"
            selectedCount={secilenParcalar.length}
          >
            <StokLimitliParcaPicker
              katalog={katalog}
              loading={loading}
              secilenParcalar={secilenParcalar}
              onSecilenChange={setSecilenParcalar}
              showSelectedSummary={false}
            />
          </ParcaPickerNestedModal>

          {error && (
            <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12 }}>{error}</div>
          )}

          <div className="siparis-modal-footer-btns" style={{ marginTop: 16 }}>
            {secilenParcalar.length > 0 && (
              <button type="button" className="btn btn-secondary" onClick={handleDiscardDraft}>
                Taslağı sil
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              İptal
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={secilenParcalar.length === 0 || submitting}
              onClick={handleSubmit}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>
                  Kaydediliyor...
                </>
              ) : (
                'İade Talebi Oluştur'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IadeOlusturModal;
